import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { DesktopLayout, StandingsSidebar, LiveAnswer as SharedLiveAnswer, TimerSection } from '../shared';
import { GameEndScreen } from './GameEndScreen';
import type { GameStatistics } from '../../types/game';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';

interface DrinkingGameQuestion {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  interesting?: string;
  sipPenalty?: number;
  funFact?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  isDrinkingGame?: boolean;
}

interface SipResultEntry {
  playerId: string;
  playerName: string;
  avatar?: string;
  sips: number;
  wrongAnswers: number;
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  submissions: Array<{
    playerId: string;
    answer: string;
    responseTime: number;
    isCorrect: boolean;
    points: number;
  }>;
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
  sipResults?: SipResultEntry[];
  sipPenalty?: number;
  funFact?: string;
  difficulty?: string;
  isDrinkingGame?: boolean;
}

interface LiveAnswer {
  playerId: string;
  playerName: string;
  avatar?: string;
  timestamp: number;
  answer?: string; // The selected answer/option
}

interface DrinkingGameHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: DrinkingGameQuestion;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
}

// Theme colors for Drinking Game (Party theme)
const THEME = {
  primary: '#EC4899',
  primaryLight: 'rgba(236, 72, 153, 0.15)',
  primaryBorder: 'rgba(236, 72, 153, 0.3)',
  accent: '#F59E0B',
  accentLight: 'rgba(245, 158, 11, 0.15)',
  accentBorder: 'rgba(245, 158, 11, 0.3)',
};

// Helper to check if avatar is an image URL
const isAvatarImage = (avatar?: string): boolean => {
  if (!avatar) return false;
  return avatar.startsWith('/') || avatar.startsWith('data:') || avatar.includes('.svg') || avatar.includes('media');
};

// Render avatar (either as image or emoji)
const renderAvatar = (avatar?: string, size: string = '1.5rem') => {
  if (isAvatarImage(avatar)) {
    return <img src={avatar} alt="" style={{ width: size, height: size, objectFit: 'contain' }} />;
  }
  return <span style={{ fontSize: size }}>{avatar || '👤'}</span>;
};

const DrinkingGameHostDisplay: React.FC<DrinkingGameHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  myPlayerId,
  hostIsPlaying = false,
  onGoHome,
}) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState<DrinkingGameQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(
    roomState.currentQuestion?.timeLimit || 0
  );
  const [questionResult, setQuestionResult] = useState<QuestionResult | null>(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [waitingForNewGame, setWaitingForNewGame] = useState(false);
  const [liveAnswers, setLiveAnswers] = useState<LiveAnswer[]>([]);

  // Host playing mode state
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [sipsEarned, setSipsEarned] = useState(0);
  const isSubmittingRef = useRef<boolean>(false);

  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const roomNameRef = useRef<string>(roomState.roomName);
  const playersRef = useRef(roomState.players);

  const resetHostPlayingState = () => {
    setSelectedAnswer(null);
    setAnswerSubmitted(false);
    setAnswerCorrect(null);
    setPointsEarned(0);
    setSipsEarned(0);
    setLiveAnswers([]);
    isSubmittingRef.current = false;
  };

  useEffect(() => {
    roomNameRef.current = roomState.roomName;
  }, [roomState.roomName]);

  useEffect(() => {
    playersRef.current = roomState.players;
  }, [roomState.players]);

  useEffect(() => {
    const questionId = roomState.currentQuestion?.questionId || roomState.currentQuestion?.id;
    if (previousQuestionIdRef.current !== questionId && questionId) {
      setCurrentQuestion(roomState.currentQuestion || null);
      setTimeRemaining(roomState.currentQuestion?.timeLimit || 0);
      setQuestionResult(null);
      resetHostPlayingState();
      previousQuestionIdRef.current = questionId;
    }
  }, [roomState.currentQuestion]);

  useEffect(() => {
    const handleQuestion = (data: DrinkingGameQuestion) => {
      if (data.isDrinkingGame) {
        setCurrentQuestion(data);
        setTimeRemaining(data.timeLimit);
        setQuestionResult(null);
        resetHostPlayingState();
        previousQuestionIdRef.current = data.questionId || data.id;
      }
    };

    const handleQuestionResult = (result: QuestionResult) => {
      if (result.isDrinkingGame) {
        setQuestionResult(result);
        setCurrentQuestion(null);
      }
    };

    const handleGameEnded = (data: {
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: GameStatistics;
    }) => {
      setGameEnded(true);
      const sortedLeaderboard = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sortedLeaderboard);
      setGameStatistics(data.statistics || null);
      setCurrentQuestion(null);
      setQuestionResult(null);
    };

    const handleReturnToWaiting = () => {
      setWaitingForNewGame(true);
      setGameEnded(false);
      setCurrentQuestion(null);
      setQuestionResult(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
    };

    const handlePlayerAnswered = (data: LiveAnswer) => {
      setLiveAnswers((prev) => {
        if (prev.some((a) => a.playerId === data.playerId)) return prev;
        return [...prev, data];
      });
    };

    const handleAnswerReceived = (data: {
      questionId: string;
      isCorrect: boolean;
      points: number;
      sipsEarned?: number;
    }) => {
      if (hostIsPlaying) {
        setAnswerCorrect(data.isCorrect);
        setPointsEarned(data.points);
        setSipsEarned(data.sipsEarned || 0);
        setAnswerSubmitted(true);
      }
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('return-to-waiting', handleReturnToWaiting);
    socket.on('player-answered', handlePlayerAnswered);
    socket.on('answer-received', handleAnswerReceived);

    return () => {
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('return-to-waiting', handleReturnToWaiting);
      socket.off('player-answered', handlePlayerAnswered);
      socket.off('answer-received', handleAnswerReceived);
    };
  }, [socket, hostIsPlaying]);

  useEffect(() => {
    if (!currentQuestion || questionResult) return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 100) {
          clearInterval(interval);
          return 0;
        }
        return prev - 100;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentQuestion, questionResult]);

  const handleAnswerClick = (answer: string) => {
    if (answerSubmitted || !currentQuestion || isSubmittingRef.current) return;
    
    isSubmittingRef.current = true;
    setSelectedAnswer(answer);

    const questionId = currentQuestion.questionId || currentQuestion.id;
    if (!questionId) {
      isSubmittingRef.current = false;
      return;
    }

    socket.emit('submit-answer', {
      roomName: roomNameRef.current,
      questionId,
      answer,
      timestamp: Date.now(),
    });
  };

  const getPlayerAvatar = (playerId: string): string | undefined => {
    const player = playersRef.current.find((p) => p.id === playerId);
    return player?.avatar;
  };

  const handleRestartGame = () => {
    if (socket && roomState) {
      setWaitingForNewGame(true);
      setGameEnded(false);
      setFinalLeaderboard([]);
      setCurrentQuestion(null);
      setQuestionResult(null);
      resetHostPlayingState();
      socket.emit('start-game', { roomName: roomState.roomName });
    }
  };

  // Fullscreen wrapper styles
  const fullscreenStyle: React.CSSProperties = {
    minHeight: '100vh',
    width: '100%',
    background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)',
    display: 'flex',
    flexDirection: 'column',
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    padding: '2.5rem 2rem',
    paddingTop: '6rem', // ~96px - header height + breathing room
    display: 'flex',
    flexDirection: 'column',
  };

  // Game ended screen
  if (gameEnded) {
    const handlePlaySameGame = () => {
      if (socket && roomState) {
        setGameEnded(false);
        setFinalLeaderboard([]);
        setCurrentQuestion(null);
        setQuestionResult(null);
        setWaitingForNewGame(true);
        socket.emit('start-game', { roomName: roomState.roomName });
      }
    };

    return (
      <GameEndScreen
        finalLeaderboard={finalLeaderboard}
        gameStatistics={gameStatistics}
        onPlaySameGame={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection || (() => {})}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
        getPlayerAvatar={getPlayerAvatar}
      />
    );
  }

  // Waiting state
  if (waitingForNewGame || (!currentQuestion && !questionResult)) {
    return (
      <div style={fullscreenStyle}>
        <Header
          leftButton={onBackToGameSelection ? { icon: HeaderIcons.back, onClick: onBackToGameSelection, tooltip: t('gameplay.backToGameSelection') } : undefined}
          rightButton={{
            icon: HeaderIcons.refresh,
            onClick: () => window.location.reload(),
            tooltip: t('common.tooltips.refreshPage')
          }}
          roomCode={roomState.roomName}
        />
        <div style={{ ...contentStyle, alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>🍺</div>
          <h2 style={{ fontSize: '2rem' }}>{t('gameplay.hostWaitingNextQuestion')}</h2>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <span className="loading-dot" style={{ fontSize: '2rem', animationDelay: '0s' }}>🍻</span>
            <span className="loading-dot" style={{ fontSize: '2rem', animationDelay: '0.2s' }}>🍷</span>
            <span className="loading-dot" style={{ fontSize: '2rem', animationDelay: '0.4s' }}>🥃</span>
          </div>
        </div>
        <style>{`
          .loading-dot { display: inline-block; animation: bounce 0.6s infinite alternate; }
          @keyframes bounce { from { transform: translateY(0); } to { transform: translateY(-15px); } }
        `}</style>
      </div>
    );
  }

  // Question result screen - fullscreen who needs to drink
  if (questionResult) {
    const sipResults = questionResult.sipResults || [];
    const hasDrinkers = sipResults.length > 0;

    // Convert liveAnswers to shared format for result screen
    const resultSharedLiveAnswers: SharedLiveAnswer[] = liveAnswers.map(la => ({
      playerId: la.playerId,
      playerName: la.playerName,
      avatar: la.avatar,
      timestamp: la.timestamp,
      answer: la.answer !== undefined ? String(la.answer) : undefined,
    }));

    return (
      <div style={fullscreenStyle}>
        <Header
          leftButton={onBackToGameSelection ? { icon: HeaderIcons.back, onClick: onBackToGameSelection, tooltip: t('gameplay.backToGameSelection') } : undefined}
          rightButton={{
            icon: HeaderIcons.refresh,
            onClick: () => window.location.reload(),
            tooltip: t('common.tooltips.refreshPage')
          }}
          roomCode={roomState.roomName}
        />
        <div style={contentStyle}>
          <DesktopLayout
            leftContent={
              <div className="result-grid" style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '2rem',
                maxWidth: '1400px',
                margin: '0 auto',
                width: '100%',
              }}>
                {/* Correct Answer */}
                <div className="correct-answer-card" style={{
                  background: 'rgba(52, 211, 153, 0.15)',
                  border: '3px solid rgba(52, 211, 153, 0.3)',
                  borderRadius: '1.5rem',
                  padding: 'clamp(1.5rem, 4vw, 2rem)',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', opacity: 0.7, marginBottom: '0.5rem', color: '#fff' }}>{t('gameplay.correctAnswerLabel')}</div>
                  <div className="correct-answer-text" style={{ fontSize: 'clamp(1.8rem, 5vw, 2.5rem)', fontWeight: 'bold', color: '#34D399' }}>
                    {questionResult.correctAnswer}
                  </div>
                </div>

                {/* Who needs to drink */}
                <div className="drinking-section" style={{
                  background: hasDrinkers ? THEME.primaryLight : 'rgba(52, 211, 153, 0.1)',
                  border: `3px solid ${hasDrinkers ? THEME.primaryBorder : 'rgba(52, 211, 153, 0.3)'}`,
                  borderRadius: '1.5rem',
                  padding: 'clamp(1.5rem, 4vw, 2rem)',
                  paddingBottom: '5rem',
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                  <h2 className="drinking-title" style={{
                    textAlign: 'center',
                    marginBottom: '2rem',
                    color: hasDrinkers ? THEME.primary : '#34D399',
                    fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
                  }}>
                    {hasDrinkers ? '🍺 Time to Drink! 🍺' : '🎉 Nobody Drinks! 🎉'}
                  </h2>
                  
                  {hasDrinkers ? (
                    <div className="drinkers-grid" style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))',
                      gap: '1rem',
                      marginBottom: '5rem',
                    }}>
                      {sipResults.map((entry, index) => (
                        <div
                          key={entry.playerId}
                          className="drinker-card"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            padding: '1.25rem',
                            background: 'rgba(0,0,0,0.2)',
                            borderRadius: '1rem',
                            animation: `slideIn 0.3s ease-out ${index * 0.1}s both`,
                          }}
                        >
                          {renderAvatar(entry.avatar, 'clamp(2rem, 5vw, 3rem)')}
                          <span className="drinker-name" style={{ flex: 1, fontSize: 'clamp(1.1rem, 3vw, 1.4rem)', fontWeight: 'bold', color: '#fff' }}>
                            {entry.playerName}
                          </span>
                          <div className="sip-badge" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            background: THEME.primary,
                            padding: '0.75rem 1.25rem',
                            borderRadius: '0.75rem',
                            fontWeight: 'bold',
                            fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                          }}>
                            <span style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}>
                              {entry.sips === 1 ? '🍺' : entry.sips === 2 ? '🍺🍺' : '🍺🍺🍺'}
                            </span>
                            <span>{entry.sips} {entry.sips === 1 ? 'sip' : 'sips'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', opacity: 0.8, marginBottom: '5rem', color: '#fff' }}>
                      Everyone got it right! 🎊
                    </div>
                  )}

                  {/* Animated beer ticker at bottom - seamless loop */}
                  <div style={{
                    position: 'absolute',
                    bottom: '0.5rem',
                    left: 0,
                    right: 0,
                    height: '5.5rem',
                    paddingTop: '1rem',
                    paddingBottom: '0.5rem',
                    overflow: 'hidden',
                    marginTop: '2rem',
                  }}>
                    <div className="beer-ticker" style={{
                      display: 'flex',
                      width: 'max-content',
                      animation: 'beerTicker 20s linear infinite',
                      alignItems: 'center',
                    }}>
                      {/* First set of emojis */}
                      {['🍺', '🍻', '🥂', '🍺', '🥃', '🍷', '🍺', '🍻', '🍺', '🥂', '🍾', '🍺', '🍻', '🥃', '🍺', '🍷', '🍺', '🍻', '🥂', '🍺'].map((emoji, i) => (
                        <span 
                          key={`a-${i}`}
                          className="beer-bounce" 
                          style={{ 
                            fontSize: '2rem', 
                            padding: '0 1rem',
                            animationDelay: `${i * 0.15}s`,
                          }}
                        >
                          {emoji}
                        </span>
                      ))}
                      {/* Duplicate set for seamless loop */}
                      {['🍺', '🍻', '🥂', '🍺', '🥃', '🍷', '🍺', '🍻', '🍺', '🥂', '🍾', '🍺', '🍻', '🥃', '🍺', '🍷', '🍺', '🍻', '🥂', '🍺'].map((emoji, i) => (
                        <span 
                          key={`b-${i}`}
                          className="beer-bounce" 
                          style={{ 
                            fontSize: '2rem', 
                            padding: '0 1rem',
                            animationDelay: `${(i + 20) * 0.15}s`,
                          }}
                        >
                          {emoji}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Fun Fact */}
                {questionResult.funFact && (
                  <div className="fun-fact-card" style={{
                    background: THEME.accentLight,
                    border: `3px solid ${THEME.accentBorder}`,
                    borderRadius: '1.5rem',
                    padding: 'clamp(1.2rem, 3vw, 1.5rem)',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.75rem',
                      fontWeight: 'bold',
                      color: THEME.accent,
                      fontSize: 'clamp(1.15rem, 2.8vw, 1.4rem)',
                    }}>
                      <span>💡</span>
                      <span>{t('gameplay.drinkingGame.didYouKnow')}</span>
                    </div>
                    <div className="fun-fact-text" style={{ fontSize: 'clamp(1.15rem, 2.8vw, 1.4rem)', lineHeight: '1.6', color: '#fff' }}>
                      {questionResult.funFact}
                    </div>
                  </div>
                )}
              </div>
            }
            rightContent={
              <StandingsSidebar
                players={roomState.players}
                liveAnswers={resultSharedLiveAnswers}
                totalPlayers={roomState.players.length}
                questionId={questionResult.questionId}
                shouldUpdateScores={true}
                theme={THEME}
              />
            }
            mobileOnlyContent={
              <StandingsSidebar
                players={roomState.players}
                liveAnswers={resultSharedLiveAnswers}
                totalPlayers={roomState.players.length}
                questionId={questionResult.questionId}
                shouldUpdateScores={true}
                isMobile={true}
                theme={THEME}
              />
            }
            rightWidth="320px"
          />
        </div>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateX(-20px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes beerTicker {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @keyframes beerBounce {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            25% { transform: translateY(-12px) rotate(-8deg); }
            75% { transform: translateY(-12px) rotate(8deg); }
          }
          .beer-bounce {
            display: inline-block;
            animation: beerBounce 0.5s ease-in-out infinite;
          }
          .beer-ticker {
            will-change: transform;
          }
          
          @media (max-width: 1024px) {
            .result-grid { grid-template-columns: 1fr !important; }
            .drinking-title { font-size: 2rem !important; }
            .correct-answer-text { font-size: 2rem !important; }
            .correct-answer-card { padding: 1.5rem !important; }
            .drinking-section { padding: 1.5rem !important; }
            .drinker-card { padding: 1rem !important; }
            .drinker-name { font-size: 1.2rem !important; }
            .sip-badge { padding: 0.5rem 1rem !important; font-size: 1rem !important; }
            .sip-badge span:first-child { font-size: 1.5rem !important; }
            .fun-fact-card { padding: 1.25rem !important; }
            .fun-fact-text { font-size: 1rem !important; }
          }
          
          @media (max-width: 600px) {
            .drinking-title { font-size: 1.5rem !important; }
            .correct-answer-text { font-size: 1.5rem !important; }
            .drinkers-grid { grid-template-columns: 1fr !important; }
            .drinker-name { font-size: 1rem !important; }
          }
        `}</style>
      </div>
    );
  }

  // Question display - fullscreen
  const progress = currentQuestion ? (timeRemaining / currentQuestion.timeLimit) * 100 : 0;
  const secondsRemaining = Math.ceil(timeRemaining / 1000);

  // Convert liveAnswers to shared format
  const sharedLiveAnswers: SharedLiveAnswer[] = liveAnswers.map(la => ({
    playerId: la.playerId,
    playerName: la.playerName,
    avatar: la.avatar,
    timestamp: la.timestamp,
    answer: la.answer,
  }));

  return (
    <div className="app">
      <Header
        leftButton={{
          icon: HeaderIcons.back,
          onClick: onBackToGameSelection || (() => {}),
          tooltip: t('gameplay.backToGameSelection')
        }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: handleRestartGame,
          tooltip: t('gameplay.restartGame')
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div className="header-game-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.gameLabel')}</span>
              <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#F8FAFC' }}>
                🍺 Drinking Game
              </span>
            </div>
            <div className="header-game-name" style={{ 
              width: '1px', 
              height: '24px', 
              backgroundColor: 'rgba(255, 255, 255, 0.3)' 
            }}></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.roomCodeLabel')}</span>
              <div style={{ 
                fontSize: '1.3rem', 
                fontWeight: 'bold', 
                letterSpacing: '0.15rem',
                color: THEME.primary,
                padding: '0.3rem 0.8rem',
                backgroundColor: THEME.primaryLight,
                borderRadius: '6px',
                border: `2px solid ${THEME.primaryBorder}`,
              }}>
                {roomState.roomName}
              </div>
            </div>
          </div>
        }
      />

      {/* Main game layout - desktop: left (question) | right (standings), mobile: stacked */}
      <div style={{
        minHeight: 'calc(100vh - 82px)',
        width: '100%',
        padding: '1rem',
        paddingTop: '1rem',
        paddingBottom: '1rem',
      }}>
        <DesktopLayout
          leftContent={
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Main Challenge Display */}
        <div 
          className="container game-display-container" 
          style={{ 
            paddingTop: '1rem',
            paddingBottom: '1rem',
            marginTop: '1rem',
            marginBottom: 0,
          }}
        >
          <div className="timer-section" style={{ width: '100%', marginBottom: '1.2rem' }}>
            <TimerSection
              questionNumber={currentQuestion?.questionNumber}
              totalQuestions={currentQuestion?.totalQuestions}
              timeSeconds={secondsRemaining}
              progress={progress}
              primaryColor={THEME.primary}
            />
          </div>

          {/* Question text - using same font size as trivia game */}
          <h1 className="game-question-text" style={{
            color: THEME.primary,
            marginBottom: '1.5rem',
            letterSpacing: '0.03em',
            textShadow: `0 2px 8px ${THEME.primaryBorder}`,
          }}>
            {currentQuestion?.question}
          </h1>

          {/* Options – only when host is playing */}
          {hostIsPlaying && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1rem',
              marginTop: '1rem',
            }}>
              {currentQuestion?.options.map((option, index) => {
                const isSelected = selectedAnswer === option;
                const showResult = answerSubmitted && answerCorrect !== null;
                const isCorrectAnswer = showResult && answerCorrect && isSelected;
                const isWrongAnswer = showResult && !answerCorrect && isSelected;

                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerClick(option)}
                    disabled={answerSubmitted}
                    className="game-option-text"
                    style={{
                      padding: '1rem 0.8rem',
                      borderRadius: '12px',
                      cursor: answerSubmitted ? 'default' : 'pointer',
                      transition: 'all 0.2s',
                      border: isSelected ? `3px solid ${THEME.primary}` : '2px solid rgba(255,255,255,0.2)',
                      background: isCorrectAnswer
                        ? 'rgba(52, 211, 153, 0.3)'
                        : isWrongAnswer
                        ? 'rgba(239, 68, 68, 0.3)'
                        : isSelected
                        ? THEME.primaryLight
                        : 'rgba(248, 250, 252, 0.2)',
                      color: 'white',
                      textAlign: 'center',
                      fontSize: '1.1rem',
                    }}
                  >
                    <span style={{ marginRight: '0.5rem', opacity: 0.7 }}>
                      {String.fromCharCode(65 + index)}.
                    </span>
                    {option}
                  </button>
                );
              })}
            </div>
          )}

          {/* Host Playing Feedback */}
          {hostIsPlaying && answerSubmitted && (
            <div style={{
              background: answerCorrect ? 'rgba(52, 211, 153, 0.15)' : 'rgba(239, 68, 68, 0.15)',
              border: `3px solid ${answerCorrect ? 'rgba(52, 211, 153, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              borderRadius: '12px',
              padding: '1.5rem',
              textAlign: 'center',
              marginTop: '1rem',
            }}>
              {answerCorrect ? (
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#34D399' }}>
                  🎉 Correct! +{pointsEarned} points
                </div>
              ) : (
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#EF4444' }}>
                  🍺 Wrong! Take {sipsEarned} {sipsEarned === 1 ? 'sip' : 'sips'}!
                </div>
              )}
            </div>
          )}
        </div>
            </div>
          }
          rightContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={false}
              theme={{
                primary: THEME.primary,
                primaryLight: THEME.primaryLight,
                primaryBorder: THEME.primaryBorder,
                accent: THEME.accent,
                accentLight: THEME.accentLight,
                accentBorder: THEME.accentBorder,
              }}
            />
          }
          mobileOnlyContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={false}
              isMobile={true}
              theme={{
                primary: THEME.primary,
                primaryLight: THEME.primaryLight,
                primaryBorder: THEME.primaryBorder,
                accent: THEME.accent,
                accentLight: THEME.accentLight,
                accentBorder: THEME.accentBorder,
              }}
            />
          }
          rightWidth="320px"
        />
      </div>
    </div>
  );
};

export default DrinkingGameHostDisplay;
